/* eslint-disable no-console */
import { useEffect, useRef } from 'react';

/**
 * useWhyDidYouUpdate - Hook de debugging para detectar causas de re-renders
 *
 * Rastrea qué props han cambiado entre renders para identificar la causa
 * de re-renders innecesarios.
 *
 *
 * Rastrea qué props han cambiado entre renders para identificar la causa
 * de re-renders innecesarios.
 *
 * @param {string} name - Nombre del componente para logging
 * @param {Object} props - Props a rastrear
 */
const useWhyDidYouUpdate = (name, props) => {
  const previousProps = useRef();

  useEffect(() => {
    if (previousProps.current) {
      const allKeys = Object.keys({ ...previousProps.current, ...props });
      const changedProps = {};

      allKeys.forEach((key) => {
        if (previousProps.current[key] !== props[key]) {
          changedProps[key] = {
            from: previousProps.current[key],
            to: props[key]
          };
        }
      });

      if (Object.keys(changedProps).length) {
        console.group(`🔄 ${name} - Props que cambiaron:`);
        Object.entries(changedProps).forEach(([key, { from, to }]) => {
          console.log(`📝 ${key}:`, { from, to });
          if (typeof from === 'object' && from !== null && typeof to === 'object' && to !== null) {
            const fromString = safeStringify(from);
            const toString = safeStringify(to);
            if (fromString !== toString) {
              console.log(`🔍 ${key} contenido cambió:`, {
                fromJSON: fromString,
                toJSON: toString
              });
            } else {
              console.log(`⚠️ ${key} referencia cambió pero contenido igual`);
            }
          }
        });
        console.groupEnd();
      }
    }

    previousProps.current = props;
  });
};

/**
 * useRenderCount - Hook simple para contar renders
 *
 * @param {string} componentName - Nombre del componente
 * @returns {number} Número de renders
 */
const useRenderCount = (componentName) => {
  const renderCount = useRef(0);

  useEffect(() => {
    renderCount.current += 1;
    console.log(`🔢 ${componentName} render #${renderCount.current}`);
  });

  return renderCount.current;
};

/**
 * useTraceUpdate - Hook avanzado para rastrear actualizaciones
 *
 * Combina useWhyDidYouUpdate y useRenderCount con información adicional
 *
 * @param {string} componentName - Nombre del componente
 * @param {Object} props - Props a rastrear
 * @param {Object} options - Opciones de configuración
 */
const useTraceUpdate = (componentName, props, options = {}) => {
  const {
    enabled = process.env.NODE_ENV === 'development',
    logRenderCount = true,
    logPropChanges = true,
    logStackTrace = false
  } = options;

  const renderCount = useRef(0);
  const previousProps = useRef();
  const startTime = useRef(Date.now());

  useEffect(() => {
    if (!enabled) return;

    renderCount.current += 1;
    const currentTime = Date.now();
    const timeSinceMount = currentTime - startTime.current;

    if (logRenderCount) {
      console.log(
        `🚀 ${componentName} render #${renderCount.current} (${timeSinceMount}ms desde mount)`
      );
    }

    if (logPropChanges && previousProps.current) {
      const allKeys = Object.keys({ ...previousProps.current, ...props });
      const changedProps = {};

      allKeys.forEach((key) => {
        if (previousProps.current[key] !== props[key]) {
          changedProps[key] = {
            from: previousProps.current[key],
            to: props[key],
            type: typeof props[key],
            isFunction: typeof props[key] === 'function',
            isObject: typeof props[key] === 'object' && props[key] !== null
          };
        }
      });

      if (Object.keys(changedProps).length) {
        console.group(`🔄 ${componentName} - Análisis de cambios:`);
        Object.entries(changedProps).forEach(([key, changeData]) => {
          const {
            from,
            to,
            type,
            isFunction,
            isObject
          } = changeData;
          console.log(`📝 ${key} (${type}):`, { from, to });

          if (isFunction) {
            console.log(`⚠️ ${key} es una función - posible causa de re-render`);
          } else if (isObject) {
            const fromStr = safeStringify(from);
            const toStr = safeStringify(to);
            if (fromStr === toStr) {
              console.log(`🔥 ${key} - REFERENCIA cambió pero CONTENIDO igual (problema de memoización)`);
            } else {
              console.log(`✅ ${key} - contenido realmente cambió`);
            }
          }
        });

        if (logStackTrace) {
          console.trace('Stack trace del re-render:');
        }

        console.groupEnd();
      }
    }

    previousProps.current = props;
  });

  return {
    renderCount: renderCount.current,
    timeSinceMount: Date.now() - startTime.current
  };
};

const safeStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch {
    return '[Circular]';
  }
};

export { useWhyDidYouUpdate, useRenderCount, useTraceUpdate };
